import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0;
import { action, computed, observable } from 'mobx';
import { isInSubnet } from 'core/util/ip';
import { fetchBgpSubPrefixes, fetchBgpReachabilityTimeseries, fetchBgpASPathGraphTimeseries } from './bgpRequests';

/** Minimal root store shape for cross-store access */
let BgpStore = (_class = class BgpStore {
  constructor() {
    /** Set by root store constructor — provides cross-store access */
    this.store = void 0;
    _initializerDefineProperty(this, "subPrefixesLoading", _descriptor, this);
    _initializerDefineProperty(this, "subPrefixesData", _descriptor2, this);
    _initializerDefineProperty(this, "subPrefixesError", _descriptor3, this);
    _initializerDefineProperty(this, "reachabilityTimeseriesLoading", _descriptor4, this);
    _initializerDefineProperty(this, "reachabilityTimeseriesData", _descriptor5, this);
    _initializerDefineProperty(this, "reachabilityTimeseriesError", _descriptor6, this);
    _initializerDefineProperty(this, "asPathGraphTimeseriesLoading", _descriptor7, this);
    _initializerDefineProperty(this, "asPathGraphTimeseriesData", _descriptor8, this);
    _initializerDefineProperty(this, "asPathGraphTimeseriesError", _descriptor9, this);
    _initializerDefineProperty(this, "asnColorMap", _descriptor0, this);
    // Track the next palette index to use (not observable - internal bookkeeping)
    this.nextColorIndex = 0;
  }
  reset() {
    this.subPrefixesLoading = false;
    this.subPrefixesData = null;
    this.subPrefixesError = null;
    this.reachabilityTimeseriesLoading = false;
    this.reachabilityTimeseriesData = null;
    this.reachabilityTimeseriesError = null;
    this.asPathGraphTimeseriesLoading = false;
    this.asPathGraphTimeseriesData = null;
    this.asPathGraphTimeseriesError = null;
    this.asnColorMap.clear();
    this.nextColorIndex = 0;
  }
  setSubPrefixesLoading(loading) {
    this.subPrefixesLoading = loading;
  }
  setSubPrefixesData(data) {
    this.subPrefixesData = data.map(pfx => pfx.trim());
    this.subPrefixesError = null;
  }
  setSubPrefixesError(error) {
    this.subPrefixesError = error;
    this.subPrefixesData = null;
  }
  setReachabilityTimeseriesLoading(loading) {
    this.reachabilityTimeseriesLoading = loading;
  }
  setReachabilityTimeseriesData(data) {
    this.reachabilityTimeseriesData = data;
    this.reachabilityTimeseriesError = null;

    // Register any new ASNs from the reachability response
    if (data.lookups?.asns) {
      this.registerAsns(Object.keys(data.lookups.asns));
    }
  }
  setReachabilityTimeseriesError(error) {
    this.reachabilityTimeseriesError = error;
    this.reachabilityTimeseriesData = null;
  }
  setASPathGraphTimeseriesLoading(loading) {
    this.asPathGraphTimeseriesLoading = loading;
  }
  setASPathGraphTimeseriesData(data) {
    this.asPathGraphTimeseriesData = data;
    this.asPathGraphTimeseriesError = null;

    // Register any new ASNs from the AS Path Graph response
    if (data.lookups?.asns) {
      this.registerAsns(Object.keys(data.lookups.asns));
    }
  }
  setASPathGraphTimeseriesError(error) {
    this.asPathGraphTimeseriesError = error;
    this.asPathGraphTimeseriesData = null;
  }
  get hasSubPrefixes() {
    return Boolean(this.subPrefixesData && this.subPrefixesData.length > 0);
  }
  get subPrefixesByPrefix() {
    return prefix => {
      if (!this.subPrefixesData || this.subPrefixesData.length === 0) {
        return [];
      }
      return this.subPrefixesData.filter(subPfx => isInSubnet(subPfx, prefix));
    };
  }
  get isLoading() {
    return this.subPrefixesLoading || this.reachabilityTimeseriesLoading || this.asPathGraphTimeseriesLoading;
  }

  /**
   * Register ASN IDs in the shared color map.
   * New ASNs get the next available chart color (round-robin).
   * Already-registered ASNs are skipped (stable mapping).
   */
  registerAsns(asnIds) {
    const {
      qualitativeColors
    } = this.store.$colors;
    if (!qualitativeColors || qualitativeColors.length === 0) {
      return;
    }
    const colors = [...qualitativeColors];
    for (const id of asnIds) {
      if (!this.asnColorMap.has(id)) {
        this.asnColorMap.set(id, colors[this.nextColorIndex % colors.length]);
        this.nextColorIndex += 1;
      }
    }
  }

  /**
   * Fetch sub-prefixes for the given monitored prefixes.
   * Sets subPrefixesData/subPrefixesError and manages loading state.
   */
  async fetchSubPrefixes({
    prefixes,
    includeCovered,
    startDate,
    endDate
  }) {
    this.setSubPrefixesLoading(true);
    try {
      const data = await fetchBgpSubPrefixes({
        prefixes,
        include_covered: includeCovered,
        start_date: startDate,
        end_date: endDate
      });
      this.setSubPrefixesData(data);
      return data;
    } catch (error) {
      this.setSubPrefixesError(error);
      throw error;
    } finally {
      this.setSubPrefixesLoading(false);
    }
  }

  /**
   * Fetch reachability timeseries for the given prefixes.
   * Sets reachabilityTimeseriesData/Error, registers new ASNs in the color map.
   */
  async fetchReachabilityTimeseries({
    prefixes,
    includeCovered,
    startDate,
    endDate,
    vpKeys
  }) {
    this.setReachabilityTimeseriesLoading(true);
    try {
      const data = await fetchBgpReachabilityTimeseries({
        prefixes,
        include_covered: includeCovered,
        start_date: startDate,
        end_date: endDate,
        vp_keys: vpKeys
      });
      this.setReachabilityTimeseriesData(data);
      return data;
    } catch (error) {
      this.setReachabilityTimeseriesError(error);
      throw error;
    } finally {
      this.setReachabilityTimeseriesLoading(false);
    }
  }

  /**
   * Fetch AS Path Graph timeseries for the given prefixes.
   * Sets asPathGraphTimeseriesData/Error, registers new ASNs in the color map.
   */
  async fetchASPathGraphTimeseries({
    prefixes,
    includeCovered,
    startDate,
    endDate,
    vpKeys,
    pruningPercentage
  }) {
    this.setASPathGraphTimeseriesLoading(true);
    try {
      const data = await fetchBgpASPathGraphTimeseries({
        prefixes,
        include_covered: includeCovered,
        start_date: startDate,
        end_date: endDate,
        vp_keys: vpKeys,
        pruning_percentage: pruningPercentage
      });
      this.setASPathGraphTimeseriesData(data);
      return data;
    } catch (error) {
      this.setASPathGraphTimeseriesError(error);
      throw error;
    } finally {
      this.setASPathGraphTimeseriesLoading(false);
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "subPrefixesLoading", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "subPrefixesData", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "subPrefixesError", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "reachabilityTimeseriesLoading", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "reachabilityTimeseriesData", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "reachabilityTimeseriesError", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "asPathGraphTimeseriesLoading", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "asPathGraphTimeseriesData", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "asPathGraphTimeseriesError", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "asnColorMap", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return observable.map();
  }
}), _applyDecoratedDescriptor(_class.prototype, "reset", [action], Object.getOwnPropertyDescriptor(_class.prototype, "reset"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setSubPrefixesLoading", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setSubPrefixesLoading"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setSubPrefixesData", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setSubPrefixesData"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setSubPrefixesError", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setSubPrefixesError"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setReachabilityTimeseriesLoading", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setReachabilityTimeseriesLoading"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setReachabilityTimeseriesData", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setReachabilityTimeseriesData"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setReachabilityTimeseriesError", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setReachabilityTimeseriesError"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setASPathGraphTimeseriesLoading", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setASPathGraphTimeseriesLoading"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setASPathGraphTimeseriesData", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setASPathGraphTimeseriesData"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setASPathGraphTimeseriesError", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setASPathGraphTimeseriesError"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hasSubPrefixes", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "hasSubPrefixes"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "subPrefixesByPrefix", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "subPrefixesByPrefix"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isLoading", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isLoading"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "registerAsns", [action], Object.getOwnPropertyDescriptor(_class.prototype, "registerAsns"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "fetchSubPrefixes", [action], Object.getOwnPropertyDescriptor(_class.prototype, "fetchSubPrefixes"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "fetchReachabilityTimeseries", [action], Object.getOwnPropertyDescriptor(_class.prototype, "fetchReachabilityTimeseries"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "fetchASPathGraphTimeseries", [action], Object.getOwnPropertyDescriptor(_class.prototype, "fetchASPathGraphTimeseries"), _class.prototype), _class);
export default new BgpStore();